/**
 * useFeedback.js — the bug / idea box.
 *
 * Anyone signed in submits from the floating bubble on every page
 * (layout/FeedbackButton). The inbox, its NEW count and triage live in
 * Settings ▸ Feedback behind the 'activity' tool (server/routes/feedback.js).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { queryKeys } from '../lib/queryClient';
import { snakeToCamel } from '../lib/utils';

export function useSubmitFeedback() {
  const qc = useQueryClient();
  return useMutation({
    // { message, page, device } → { ok, id }. Over 20 an hour is a 429 whose
    // sentence the ApiError carries.
    mutationFn: (body) => api.post('/feedback', body),
    // An admin's own submission shows in their inbox and badge straight away.
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.feedback.all }),
  });
}

/** @param {'new'|'done'|'all'} status */
export function useFeedbackList(status = 'all') {
  return useQuery({
    queryKey: [...queryKeys.feedback.all, 'list', status],
    queryFn: async () => (await api.get(`/feedback?status=${encodeURIComponent(status)}`)).map(snakeToCamel),
  });
}

/** Resolves to { new: <int> }. Pass enabled:false for anyone without the activity tool. */
export function useFeedbackCount({ enabled = true } = {}) {
  return useQuery({
    queryKey: [...queryKeys.feedback.all, 'count'],
    queryFn: () => api.get('/feedback/count'),
    enabled,
    refetchInterval: 60 * 1000,
  });
}

export function useUpdateFeedback() {
  const qc = useQueryClient();
  return useMutation({
    // { id, status?: 'new'|'done', kind?: 'bug'|'idea'|null } — null clears the tag.
    mutationFn: ({ id, ...patch }) => api.put(`/feedback/${encodeURIComponent(id)}`, patch),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.feedback.all });
      qc.invalidateQueries({ queryKey: queryKeys.actionLog.all });
    },
  });
}
